/**
 * Shared copy/metadata for the escalation channel selector.
 *
 * Used by every UI surface that lets the user configure where an agent should
 * escalate to (currently: the workflow builder's agent settings modal and the
 * legacy AgentEscalation page). Keeping them in one file means the channel
 * dropdown looks and reads identically wherever it appears.
 *
 * The shape mirrors the backend's `agent.escalationChannel` field. Channels:
 *   'none'    — log only, no delivery
 *   'email'   — Aegent SMTP (falls back to email_manager skill credentials)
 *   'slack'   — Slack incoming webhook URL
 *   'webhook' — generic JSON POST
 */

export const ESCALATION_CHANNELS = [
  { value: 'none', label: "Don't notify (log only)" },
  { value: 'email', label: 'Email (from Aegent)' },
  { value: 'slack', label: 'Slack incoming webhook' },
  { value: 'webhook', label: 'Generic webhook (POST JSON)' },
];

export function escalationTargetLabel(channel) {
  if (channel === 'email') return 'Recipient email';
  if (channel === 'slack') return 'Slack webhook URL';
  if (channel === 'webhook') return 'Webhook URL';
  return 'Recipient';
}

export function escalationTargetPlaceholder(channel) {
  if (channel === 'email') return 'person@example.com';
  if (channel === 'slack') return 'https://hooks.slack.com/services/T.../B.../...';
  if (channel === 'webhook') return 'https://example.com/escalations';
  return '';
}

/** Inline field hint — short, sits right under the recipient input. */
export function escalationTargetHint(channel) {
  if (channel === 'email') return 'sent from Aegent — falls back to your email_manager skill creds if Aegent SMTP isn’t set';
  if (channel === 'slack') return 'posts as whichever bot you named when creating the webhook in your Slack workspace';
  if (channel === 'webhook') return 'POSTed by the Aegent backend with header X-Aegent-Source: escalation';
  return 'pick a channel first';
}

/** Slightly longer hint shown under the dropdown itself (used by AgentEscalation). */
export function escalationChannelHint(channel) {
  if (channel === 'email') return 'Sent from Aegent (configure AEGENT_SMTP_… in .env). Falls back to your email_manager skill credentials if Aegent SMTP isn’t set.';
  if (channel === 'slack') return 'Posts as whichever bot you named when creating the incoming webhook in your Slack workspace.';
  if (channel === 'webhook') return 'POSTed by the Aegent backend with header X-Aegent-Source: escalation.';
  return 'Escalations will still be recorded in the logs — they just won’t be delivered anywhere.';
}
